import {observer} from "mobx-react";
import {Checkbox, ConfirmModalDialog, FieldAutoComplete, FieldSelect, FormSection, UploadElement} from "../../ui";
import {FmsCustomerItem} from "../../../store/fms/FmsCustomerStore";
import {DocumentTypeDto, ShipmentProcessTypeDto, ShipmentTypeDto} from "../../../api/fms";
import {convertDocumentType, displayShipmentType} from "../../../api";
import {FieldInput, FieldMultiSelect} from "@kroschke/react-ui";
import {countryStore, DialogStore} from "../../../store";
import {useState} from "react";

export interface FmsFormData {
    customer: FmsCustomerItem | undefined
    documentType: DocumentTypeDto[]
    process: ShipmentProcessTypeDto
    vin: string
    city: string
    addition: string
    country: string
    houseNumber: string
    postCode: string
    street: string
    line1: string
    line2: string
    mail: string
    phone: string
    shipmentType: ShipmentTypeDto
    completeShipping: boolean
    billable: boolean
    file?: File
}

export interface FmsFormValidation {
    vin?: string | undefined
    customer?: string | undefined
    documentType?: string | undefined
    process?: string | undefined
    city?: string | undefined
    addition?: string | undefined
    country?: string | undefined
    houseNumber?: string | undefined
    postCode?: string | undefined
    street?: string | undefined
    line1?: string | undefined
    line2?: string | undefined
    mail?: string | undefined
    phone?: string | undefined
    shipmentType?: string | undefined
    file?: string | undefined
}

interface FmsFormProps {
    disabled: boolean
    validation: FmsFormValidation
    customers: FmsCustomerItem[]
    data: FmsFormData
    onSearchCustomer: (q: string) => Promise<FmsCustomerItem[]>
    onBlur: () => any
    onChange: (value: FmsFormData) => any
}

const processOptions = [
    // Yes no "STORAGE_TRANSFER" since this is only set by STS and no INTERNAL since it is selected by address
    {
        value: ShipmentProcessTypeDto.REGULAR,
        label: 'Allgemein'
    },
    {
        value: ShipmentProcessTypeDto.MVR,
        label: 'Zulassung'
    }
]

const shipmentOptions = Object.keys(ShipmentTypeDto).map(e => ({
    value: e,
    label: displayShipmentType(e as ShipmentTypeDto)
}))

const documentDropDownOptions = Object.keys(DocumentTypeDto).map(e => ({
    value: e,
    label: convertDocumentType(e as DocumentTypeDto)
}))

const FmsCreateForm = observer(function FmsForm({
                                                    disabled,
                                                    data,
                                                    validation,
                                                    customers,
                                                    onSearchCustomer,
                                                    onBlur,
                                                    onChange,
                                                }: FmsFormProps) {
    const {
        completeShipping,
        billable,
        customer,
        vin,
        documentType,
        process,
        city,
        addition,
        country,
        houseNumber,
        postCode,
        street,
        line1,
        line2,
        mail,
        phone,
        shipmentType,
        file
    } = data
    const [confirmStore] = useState(new DialogStore())
    return <>
        <ConfirmModalDialog
            store={confirmStore}
            title={"Bitte bestätigen"}
            label={"Nicht abrechnen"}
            message={<strong>Soll der Versandauftrag wirklich kostenlos sein?</strong>}
            execute={async () => {
                onChange({
                    ...data,
                    billable: false
                })
            }}
        />
        <div style={{overflow: 'visible', padding: '1em'}}>
            <FormSection
                label={"Allgemein"}
                children={
                    <>
                        <div className={"columns"}>
                            <div className={"column is-one-third"}>
                                <FieldAutoComplete
                                    disabled={disabled}
                                    label={"Kunde"}
                                    required={true}
                                    help={validation.customer}
                                    invalid={Boolean(validation.customer)}
                                    value={customer}
                                    options={customers}
                                    itemRenderer={c => c.label}
                                    search={onSearchCustomer}
                                    onBlur={() => onBlur()}
                                    onChange={(customer: FmsCustomerItem | undefined) => {
                                        onChange({
                                            ...data,
                                            customer: customer!!,
                                        })
                                        onBlur()
                                    }}
                                />
                            </div>
                            <div className={"column is-one-third"}>
                                <FieldSelect
                                    disabled={disabled}
                                    label={'Prozess'}
                                    options={processOptions}
                                    value={process}
                                    required={true}
                                    help={validation.process}
                                    invalid={Boolean(validation.process)}
                                    onBlur={() => onBlur()}
                                    onChange={e => {
                                        onChange({
                                            ...data,
                                            process: e as ShipmentProcessTypeDto
                                        })
                                    }}
                                />
                            </div>
                            <div className={"column is-one-third"}>
                                <FieldSelect
                                    disabled={disabled}
                                    label={'Versandart'}
                                    options={shipmentOptions}
                                    value={shipmentType}
                                    required={true}
                                    help={validation.shipmentType}
                                    invalid={Boolean(validation.shipmentType)}
                                    onBlur={() => onBlur()}
                                    onChange={e => {
                                        onChange({
                                            ...data,
                                            shipmentType: e as ShipmentTypeDto
                                        })
                                    }}
                                />
                            </div>
                        </div>
                        <div className={"columns"}>
                            <div className={'column is-one-third'}>
                                <FieldInput
                                    disabled={disabled}
                                    required={true}
                                    maxLength={17}
                                    help={validation.vin}
                                    invalid={Boolean(validation.vin)}
                                    value={vin}
                                    onBlur={() => onBlur()}
                                    onChange={fin => onChange({
                                        ...data,
                                        vin: fin
                                    })}
                                    inputTransform={(v: string) => {
                                        return v.toUpperCase()
                                    }}
                                    label={"Fahrgestellnummer"}
                                />
                            </div>
                            <div className={'column is-one-third'}>
                                <FieldMultiSelect
                                    disabled={disabled || completeShipping}
                                    label={"Dokumente"}
                                    options={documentDropDownOptions}
                                    required={true}
                                    help={validation.documentType}
                                    invalid={Boolean(validation.documentType)}
                                    onBlur={() => onBlur()}
                                    value={documentType}
                                    onChange={selections => {

                                        const validSelection = selections.map(value => value as DocumentTypeDto)

                                        if (documentType.length > selections.length) {
                                            onChange({
                                                ...data,
                                                documentType: documentType.filter(doc => validSelection.includes(doc))
                                            })
                                        } else {
                                            const missingTypes = validSelection.filter(type => !(documentType.includes(type)))
                                            const updatedList = [...documentType, ...missingTypes]

                                            onChange({
                                                ...data,
                                                documentType: updatedList
                                            })
                                        }
                                    }}
                                />
                            </div>
                            <div className={'column is-one-third'}>
                                <div className={"is-flex"} style={{
                                    paddingTop: '0.8em',  gap: '1.5em'
                                }}>
                                    <Checkbox
                                        label={" Komplettversand"}
                                        disabled={disabled}
                                        value={completeShipping}
                                        onBlur={async () => onBlur()}
                                        onChange={completeShipping => {
                                            onChange({
                                                ...data,
                                                completeShipping
                                            })
                                        }}
                                    />
                                    <Checkbox
                                        label={" Abrechenbar"}
                                        disabled={disabled}
                                        value={billable}
                                        onBlur={async () => onBlur()}
                                        onChange={billable => {
                                                if (!billable) {
                                                    confirmStore.dialogOpen = true
                                                } else {
                                                    onChange({
                                                        ...data,
                                                        billable: billable
                                                    })
                                                }
                                            }
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                }
            />
            <FormSection
                label={"Empfänger"}
                children={
                    <>
                        <div className={"columns"}>
                            <div className={'column is-one-third'}>
                                <FieldInput
                                    disabled={disabled}
                                    required={true}
                                    help={validation.line1}
                                    invalid={Boolean(validation.line1)}
                                    value={line1}
                                    onBlur={() => onBlur()}
                                    onChange={val => onChange({
                                        ...data,
                                        line1: val
                                    })}
                                    label={"Adresszeile 1"}
                                />
                            </div>
                            <div className={'column is-one-third'}>
                                <FieldInput
                                    disabled={disabled}
                                    required={false}
                                    help={validation.line2}
                                    invalid={Boolean(validation.line2)}
                                    value={line2}
                                    onBlur={() => onBlur()}
                                    onChange={val => onChange({
                                        ...data,
                                        line2: val
                                    })}
                                    label={"Adresszeile 2"}
                                />
                            </div>
                            <div className={'column is-one-third'}></div>
                        </div>
                        <div className={"columns"}>
                            <div className={'column is-one-third'}>
                                <FieldInput
                                    disabled={disabled}
                                    required={true}
                                    help={validation.street}
                                    invalid={Boolean(validation.street)}
                                    value={street}
                                    onBlur={() => onBlur()}
                                    onChange={val => onChange({
                                        ...data,
                                        street: val
                                    })}
                                    label={"Straße"}
                                />
                            </div>
                            <div className={'column is-one-third'}>
                                <FieldInput
                                    disabled={disabled}
                                    required={false}
                                    help={validation.houseNumber}
                                    invalid={Boolean(validation.houseNumber)}
                                    value={houseNumber}
                                    onBlur={() => onBlur()}
                                    onChange={val => onChange({
                                        ...data,
                                        houseNumber: val
                                    })}
                                    label={"Hausnummer"}
                                />
                            </div>
                            <div className={'column is-one-third'}>
                                <FieldInput
                                    disabled={disabled}
                                    required={false}
                                    help={validation.addition}
                                    invalid={Boolean(validation.addition)}
                                    value={addition}
                                    onBlur={() => onBlur()}
                                    onChange={val => onChange({
                                        ...data,
                                        addition: val
                                    })}
                                    label={"Adresszusatz"}
                                />
                            </div>
                        </div>

                        <div className={"columns"}>
                            <div className={'column is-one-third'}>
                                <FieldInput
                                    disabled={disabled}
                                    required={true}
                                    help={validation.postCode}
                                    invalid={Boolean(validation.postCode)}
                                    value={postCode}
                                    onBlur={() => onBlur()}
                                    onChange={val => onChange({
                                        ...data,
                                        postCode: val
                                    })}
                                    label={"PLZ"}
                                />
                            </div>
                            <div className={'column is-one-third'}>
                                <FieldInput
                                    disabled={disabled}
                                    required={true}
                                    help={validation.city}
                                    invalid={Boolean(validation.city)}
                                    value={city}
                                    onBlur={() => onBlur()}
                                    onChange={val => onChange({
                                        ...data,
                                        city: val
                                    })}
                                    label={"Ort"}
                                />
                            </div>
                            <div className={'column is-one-third'}>
                                <FieldSelect
                                    disabled={disabled}
                                    label={"Land"}
                                    invalid={Boolean(validation.country)}
                                    help={validation.country}
                                    options={countryStore.options}
                                    value={country}
                                    onBlur={() => onBlur()}
                                    onChange={val => {
                                        onChange({
                                            ...data,
                                            country: val
                                        })
                                    }}
                                    required={true}
                                />
                            </div>
                        </div>
                        <div className={"columns"}>
                            <div className={"column is-one-third"}>
                                <FieldInput
                                    disabled={disabled}
                                    required={false}
                                    help={validation.mail}
                                    invalid={Boolean(validation.mail)}
                                    value={mail}
                                    onBlur={() => onBlur()}
                                    onChange={val => onChange({
                                        ...data,
                                        mail: val
                                    })}
                                    label={"Email"}
                                />
                            </div>
                            <div className={"column is-one-third"}>
                                <FieldInput
                                    disabled={disabled}
                                    required={false}
                                    help={validation.phone}
                                    invalid={Boolean(validation.phone)}
                                    value={phone}
                                    onBlur={() => onBlur()}
                                    onChange={val => onChange({
                                        ...data,
                                        phone: val
                                    })}
                                    label={"Telefon"}
                                />
                            </div>
                        </div>
                    </>
                }
            />
            <FormSection
                label={"Anschreiben"}
                children={
                    <div className={"columns"} style={{flex: '1'}} >
                        <div className={"column is-half"}>
                            <UploadElement
                                framed={true}
                                value={file}
                                formatsString={'pdf'}
                                acceptedTypes={[
                                    'application/pdf'
                                ]}
                                upload={async (f) => {
                                    onChange({
                                        ...data,
                                        file: f
                                    })
                                    await onBlur()
                                }}
                                fileError={async () => {
                                    onChange({
                                        ...data,
                                        file: undefined
                                    })
                                    await onBlur()
                                }}
                            />
                        </div>
                    </div>
                }
            />
        </div>

    </>
})

export {
    FmsCreateForm
}
